import { Theme } from '@material-ui/core/styles';

/**
 * Semantic colour tokens for the onboarding plugin.
 *
 * Light-mode values are the original design.
 * Dark-mode values are adjusted for contrast / readability on dark backgrounds.
 */
export interface OnboardingColors {
  /* ── Brand / primary accent ──────────────── */
  /** Primary accent (links, active titles, buttons) */
  primary: string;
  /** Darker primary for hover states */
  primaryHover: string;

  /* ── Semantic status ─────────────────────── */
  /** Success / completed / "you can" */
  success: string;
  /** Error / prohibited / "you cannot" */
  error: string;

  /* ── Surface & borders ───────────────────── */
  /** Badge / chip background */
  badgeBg: string;
  /** Badge / chip border */
  badgeBorder: string;
  /** Badge / chip text */
  badgeText: string;
  /** Badge / chip alternative text */
  badgeAltText: string;

  /** Warning banner background */
  warningBannerBg: string;
  /** Warning banner icon & text */
  warningBannerFg: string;

  /** Warning/info box background (yellow) */
  warningBoxBg: string;
  /** Warning/info box border */
  warningBoxBorder: string;

  /** Info box background (grey) */
  infoBoxBg: string;
  /** Info box border */
  infoBoxBorder: string;

  /** Placeholder image background */
  placeholderBg: string;

  /* ── Text shades ─────────────────────────── */
  /** Icon colour (state icons, role icons) */
  iconDefault: string;
  /** Subtitle / caption / tips text */
  subtitleText: string;

  /* ── Modal ───────────────────────────────── */
  /** Modal header background */
  modalHeaderBg: string;
  /** Modal header text */
  modalHeaderFg: string;

  /* ── Flow diagram ────────────────────────── */
  /** FlowBox border & FlowArrow icon colour */
  flowAccent: string;
  /** FlowBox subtitle text */
  flowSubtitle: string;

  /* ── Spotlight (fixed) ───────────────────── */
  spotlightGlow: string;
  spotlightBackdrop: string;

  /* ── Cards ───────────────────────────────── */
  /** Card / link-card border */
  cardBorder: string;

  /* ── Buttons ─────────────────────────────── */
  buttonPrimaryBg: string;
  buttonPrimaryFg: string;
}

const lightColors: OnboardingColors = {
  primary: '#17668c',
  primaryHover: '#105070',

  success: '#00961B',
  error: '#d32f2f',

  badgeBg: '#eceff1',
  badgeBorder: '#cfd8dc',
  badgeText: '#455a64',
  badgeAltText: '#ffffff',

  warningBannerBg: '#FDD291',
  warningBannerFg: '#000000',

  warningBoxBg: '#fff3cd',
  warningBoxBorder: '#ffc107',

  infoBoxBg: '#f0f7fa',
  infoBoxBorder: '#d4e6ed',

  placeholderBg: '#e0e0e0',

  iconDefault: '#37474f',
  subtitleText: '#546e7a',

  modalHeaderBg: '#2C3E50',
  modalHeaderFg: '#ffffff',

  flowAccent: '#17668c',
  flowSubtitle: '#546e7a',

  spotlightGlow: 'rgba(255,255,255,0.6)',
  spotlightBackdrop: 'rgba(0,0,0,0.6)',

  cardBorder: '#eaeaea',

  buttonPrimaryBg: '#17668c',
  buttonPrimaryFg: '#ffffff',
};

const darkColors: OnboardingColors = {
  primary: '#4fc3f7',
  primaryHover: '#81d4fa',

  success: '#66bb6a',
  error: '#ef5350',

  badgeBg: '#263238',
  badgeBorder: '#37474f',
  badgeText: '#b0bec5',
  badgeAltText: '#000000',

  warningBannerBg: '#3e2723',
  warningBannerFg: '#ffcc80',

  warningBoxBg: '#3e2723',
  warningBoxBorder: '#ff8f00',

  infoBoxBg: '#1a2733',
  infoBoxBorder: '#2a3f4f',

  placeholderBg: '#2c2c2c',

  iconDefault: '#b0bec5',
  subtitleText: '#90a4ae',

  modalHeaderBg: '#0d1b2a',
  modalHeaderFg: '#e0e0e0',

  flowAccent: '#4fc3f7',
  flowSubtitle: '#90a4ae',

  spotlightGlow: 'rgba(255,255,255,0.35)',
  spotlightBackdrop: 'rgba(0,0,0,0.75)',

  cardBorder: '#3a3a3a',

  buttonPrimaryBg: '#4fc3f7',
  buttonPrimaryFg: '#000000',
};

/**
 * Returns the correct color palette based on the current MUI theme mode.
 * Use inside `makeStyles(theme => ...)` or with `useTheme()`.
 */
export const getOnboardingColors = (theme: Theme): OnboardingColors =>
  theme.palette.type === 'dark' ? darkColors : lightColors;
